import { Box, CircularProgress, Container, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useGetAssetGroupAvailabilitiesQuery } from '../../../api/group/assetGroupApiSlice';
import { useGetLocationListQuery } from '../../../api/locations/locationsApiSlice';
import { Location } from '../../../api/types';
import Logger from '../../../common/logger';
import CustomButton from '../../../components/CustomButton';
import CustomerMailBlockedNote from '../../../components/booking/CustomerMailBlockedNote';
import { customerMailRefusalOf, mailBlockedOf, savedOutcomeToast } from '../../../utils/customerMailGuard';
import NonRoomRentalReservationSummary from '../../../components/NonRoomRentalReservationSummary';
import PriceAdjustmentReasonField from '../../../components/PriceAdjustmentReasonField';
import { usePriceAdjustmentAudit } from '../../../hooks/usePriceAdjustmentAudit';
import { useFetchGroups } from '../../../flows/fetchGroupsFlow';
import { paths } from '../../../routes';
import { bookingApiSlice } from '../../../api/booking/bookingApiSlice';
import { useCreateNonRoomBookingMutation } from '../../../api/booking/bookingWriteApiSlice';
import {
    clearBookingForm,
    resetReservationData,
    setAssetGroupWithAvailabilities,
    setComment,
    setDiscount,
    setDuration,
    setEmailNote,
    setEndDate,
    setPersonalDetail,
    setQuantity,
    setSelectedAssetGroup,
    setSelectedLocation,
    setStartDate,
    setStartTime,
} from '../../../store/slices/bookingCreationNonRoomRentalSlice';
import { RootState } from '../../../store/store';
import { PricePreviewArgs } from '../../../api/booking/pricePreviewApiTypes';
import { usePricePreview } from '../../../hooks/usePricePreview';
import { PersonalDataValidation } from '../../../types/bookingCreation';
import { NonRoomUseCase } from '../../../types/bookingCreationNonRoomRental';
import { BOOKING_EMAIL_NOTE_MAX_LENGTH, getUniqueGroupNamesByService } from '../../../utils';
import BookingDatePicker from '../../common/BookingDatePicker';
import CustomModal from '../../common/CustomModal';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import SnackbarComponent from '../../common/SnackbarComponent';
import InputField from '../../../components/ui/InputField';
import BookingAttributionFields, {
    BookingAttribution,
    EMPTY_BOOKING_ATTRIBUTION,
} from '../../../components/booking/BookingAttributionFields';
import { attributionPayload } from '../../../utils/costCenter';
import SelectField from '../../../components/ui/SelectField';
import { stationDateTime } from '../../../utils/stationTime';
import { errorMessageFrom } from '../../../utils/errorMessage';
import { useLocationTimeZone } from '../../../hooks/useLocationTimeZone';
import {
    deriveAvailableDurationOptions,
    deriveAvailableStartDates,
    deriveAvailableTimeSlots,
} from '../../../utils/nonRoomAvailability';

// Per-use-case wording. The flow itself is identical for every non-room use
// case (generalize, don't fork) — only labels differ.
//
// A booking is for one unit (PO, 2026-10-30, #1528): the station hands out one
// Fach per booking at "Nutzung starten", so there is no quantity selector, and
// `POST /admin/order` refuses a quantity above 1. Where several units of one
// group are commonly booked together (products, boards), a hint says to create
// one booking each.
const USE_CASE_UI: Record<NonRoomUseCase, {
    groupLabelKey: string;
    noLocationsKey: string;
    noGroupsKey: string;
    showsOneUnitHint: boolean;
}> = {
    storage: {
        groupLabelKey: 'create_booking.reservation_details.locker_size',
        noLocationsKey: 'create_booking.reservation_details.no_storage_locations',
        noGroupsKey: 'create_booking.reservation_details.no_lockers_available',
        showsOneUnitHint: false,
    },
    rental: {
        groupLabelKey: 'create_booking.reservation_details.product',
        noLocationsKey: 'create_booking.reservation_details.no_rental_locations',
        noGroupsKey: 'create_booking.reservation_details.no_products_available',
        showsOneUnitHint: true,
    },
    sup: {
        groupLabelKey: 'create_booking.reservation_details.sup_group',
        noLocationsKey: 'create_booking.reservation_details.no_sup_locations',
        noGroupsKey: 'create_booking.reservation_details.no_sup_groups',
        showsOneUnitHint: true,
    },
};

interface CreateNonRoomBookingPageProps {
    useCase: NonRoomUseCase;
}

const CreateNonRoomBookingPage = ({ useCase }: CreateNonRoomBookingPageProps) => {
    const styles = {
        paper: () => ({
            padding: '20px',
        }),
    };
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ui = USE_CASE_UI[useCase];

    const [open, setOpen] = useState(false);
    // #1810: organisation (unit) and the invoice recipient's cost centre.
    const [attribution, setAttribution] = useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingCreateBookingOnlySave, setLoadingCreateBookingOnlySave] = useState(false);
    const [loadingCreateBookingSaveAndSend, setLoadingCreateBookingSaveAndSend] = useState(false);
    const [createNonRoomBooking] = useCreateNonRoomBookingMutation();

    const [snackbarData, setSnackbarData] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({ open: false, message: '', severity: 'success' });

    const personalDetails = useSelector((state: RootState) => state.bookingCreationNonRoomRental.personalDetails);
    const startDate = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.startDate);
    const startTime = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.startTime);
    const endDate = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.endDate);
    const selectedLocation = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.selectedLocation);
    const comment = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.comment);
    const emailNote = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.emailNote);
    const discount = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.discount);
    // #3332 D3: why the price was adjusted, as on room and venue bookings. Stored
    // on the inline adjustment voucher and shown in the price adjustment list.
    const {
        reason: priceAdjustmentReason,
        setReason: setPriceAdjustmentReason,
        payload: priceAdjustmentAudit,
    } = usePriceAdjustmentAudit(discount);
    const personalDataValidation: PersonalDataValidation = useSelector((state: RootState) => state.bookingCreationNonRoomRental.personalDetailsValidation);
    const assetGroups = useSelector((state: RootState) => state.bookingCreationNonRoomRental.assetGroups);
    const selectedGroup = useSelector((state: RootState) => state.bookingCreationNonRoomRental.selectedGroup);
    const assetGroupWithAvailabilities = useSelector((state: RootState) => state.bookingCreationNonRoomRental.assetGroupWithAvailabilities);
    const duration = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.duration);
    const quantity = useSelector((state: RootState) => state.bookingCreationNonRoomRental.reservationDetails.quantity);
    const config = useSelector((state: RootState) => state.authentication.config);
    // #3219: slots, days and the hand-back weekday are the picked location's clock.
    const timeZone = useLocationTimeZone(selectedLocation?.id);

    // Reset the shared form state when the use case route changes, so a
    // half-filled storage draft can't bleed into the SUP create form.
    useEffect(() => {
        dispatch(clearBookingForm());
    }, [useCase, dispatch]);

    // Calculate date range for availability API call
    // From: Now (current date and time), To: Last day of next month
    const dateRangeForAvailabilities = useMemo(() => {
        const from = dayjs().toISOString(); // Current date and time
        const to = dayjs().add(1, 'month').endOf('month').toISOString();
        return { from, to };
    }, []);

    // Fetch availabilities when group is selected
    const { data: assetGroupWithAvailabilitiesData, isFetching: fetchingAvailabilities } = useGetAssetGroupAvailabilitiesQuery(
        {
            groupId: selectedGroup?.id ?? '',
            fromDate: dateRangeForAvailabilities.from,
            toDate: dateRangeForAvailabilities.to,
        },
        { skip: !selectedGroup?.id }, // Only call when group is selected
    );

    useEffect(() => {
        if (assetGroupWithAvailabilitiesData) {
            dispatch(setAssetGroupWithAvailabilities({ data: assetGroupWithAvailabilitiesData }));
        }
    }, [assetGroupWithAvailabilitiesData, dispatch]);

    // Availability-derived option lists. The derivations are shared with the
    // booking-edit dialog (`BookingDetailsForNonRoomRental`) so both screens
    // offer exactly the same windows — see `utils/nonRoomAvailability`.
    const availableStartDates = useMemo(
        () => deriveAvailableStartDates(assetGroupWithAvailabilities?.availability),
        [assetGroupWithAvailabilities],
    );

    const availableTimeSlots = useMemo(
        () => deriveAvailableTimeSlots(assetGroupWithAvailabilities?.availability, startDate),
        [assetGroupWithAvailabilities, startDate],
    );

    const availableDurationOptions = useMemo(
        () => deriveAvailableDurationOptions({
            availability: assetGroupWithAvailabilities?.availability,
            startDate,
            startTime,
            skipEndingInDays: config?.skipEndingInDays,
            timeZone,
            t,
        }),
        [assetGroupWithAvailabilities, startDate, startTime, config, t, timeZone],
    );

    const groupOptions = useMemo(() => {
        return getUniqueGroupNamesByService(assetGroups, useCase);
    }, [assetGroups, useCase]);

    const { data: locations, isLoading: loadingLocations } = useGetLocationListQuery(useCase);
    const fetchGroups = useFetchGroups(selectedLocation?.id);

    // Auto-select location if only one is available
    useEffect(() => {
        if (locations && locations.length === 1 && !selectedLocation) {
            dispatch(setSelectedLocation({ location: locations[0] }));
        }
    }, [locations, selectedLocation, dispatch]);

    useEffect(() => {
        const getGroups = async () => {
            if (selectedLocation?.id && fetchGroups) {
                setLoadingGroups(true);
                await fetchGroups();
                setLoadingGroups(false);
            }
        };
        getGroups();
    }, [selectedLocation?.id, fetchGroups]);

    const resetSelectionBelow = (level: 'group' | 'date' | 'time' | 'duration') => {
        if (level === 'group' || level === 'date') {
            dispatch(setStartTime({ startTime: '' }));
        }
        if (level !== 'duration') {
            dispatch(setDuration({ duration: 0 }));
        }
        dispatch(setEndDate({ endDate: null }));
        dispatch(setQuantity({ quantity: 1 }));
        dispatch(setDiscount({ discount: 0 }));
    };

    const handleStartDateChange = (start: Dayjs | null) => {
        if (start) {
            dispatch(setStartDate({ startDate: start.startOf('day') }));
            resetSelectionBelow('date');
        }
    };
    const onGroupChange = (groupName: string) => {
        const group = assetGroups?.find((g) => g.name === groupName);
        dispatch(setSelectedAssetGroup({ group: group ?? null }));
        dispatch(setStartDate({ startDate: null }));
        resetSelectionBelow('group');
    };
    const handleStartTimeChange = (time: string) => {
        dispatch(setStartTime({ startTime: time }));
        resetSelectionBelow('time');
    };

    const handleDurationChange = (value: string) => {
        const durationMinutes = parseInt(value, 10);
        dispatch(setDuration({ duration: durationMinutes }));
        dispatch(setQuantity({ quantity: 1 }));
        dispatch(setDiscount({ discount: 0 }));

        if (startDate && startTime && durationMinutes) {
            // The picked slot on the station's clock (the availability slots are
            // station wall-clock), then the duration.
            const startDateTime = stationDateTime(startDate, startTime, timeZone);
            const endDateTime = startDateTime.add(durationMinutes, 'minutes');
            dispatch(setEndDate({ endDate: endDateTime }));
        }
    };

    const onLocationChange = (locationName: string) => {
        const location = locations?.find((loc: Location) => loc.name === locationName);
        dispatch(setSelectedLocation({ location: location ?? null }));
        dispatch(resetReservationData());
    };
    const onFirstNameChange = (firstName: string) => dispatch(setPersonalDetail({ field: 'firstName', value: firstName }));
    const onLastNameChange = (lastName: string) => dispatch(setPersonalDetail({ field: 'lastName', value: lastName }));
    const onPhoneChange = (phone: string) => {
        dispatch(setPersonalDetail({ field: 'phone', value: phone }));
    };
    const onEmailChange = (email: string) => dispatch(setPersonalDetail({ field: 'email', value: email }));

    const onCommentChange = (newComment: string) => {
        dispatch(setComment({ comment: newComment }));
    };

    const onEmailNoteChange = (newEmailNote: string) => {
        dispatch(setEmailNote({ emailNote: newEmailNote }));
    };

    const handleCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false });
    };

    // Combine startDate with startTime for display in summary
    const startDateTimeForSummary = useMemo(() => {
        if (!startDate || !startTime) return null;
        return stationDateTime(startDate, startTime, timeZone).toISOString();
    }, [startDate, startTime, timeZone]);

    // Booking price single source of truth (#1203): the price box renders the
    // backend breakdown (group-based; duration derived from the dates,
    // multiplied by the booked units).
    const previewArgs: PricePreviewArgs | undefined = useMemo(() => {
        const groupId = selectedGroup?.id;
        if (!groupId || !startDateTimeForSummary || !endDate) return undefined;
        return {
            useCase,
            groupId,
            checkIn: startDateTimeForSummary,
            checkOut: endDate.toISOString(),
            units: quantity,
            discount,
        };
    }, [useCase, selectedGroup?.id, startDateTimeForSummary, endDate, quantity, discount]);

    const { breakdown, previewStatus } = usePricePreview(previewArgs);
    // PO, 2026-10-30: the backend says whether this booking's customer mail
    // may go out; "save" stays available either way.
    const mailRefusal = customerMailRefusalOf(breakdown?.customerMail);

    const handleCreateBooking = async (mode: 'save_only' | 'save_and_send_email') => {
        if (!selectedGroup || !startDate || !endDate || !startTime || quantity < 1) {
            setSnackbarData({
                open: true,
                message: t('create_booking.error.missing_fields'),
                severity: 'error',
            });
            return;
        }

        if (mode === 'save_only') {
            setLoadingCreateBookingOnlySave(true);
        } else {
            setLoadingCreateBookingSaveAndSend(true);
        }

        try {
            // The picked slot on the station's clock — same instant as the summary.
            const fromDate = stationDateTime(startDate, startTime, timeZone).toISOString();

            // The backend recomputes the price and derives the paid state
            // server-side — no total / isFullyDiscounted in the payload.
            const created = await createNonRoomBooking({
                useCase,
                groupId: selectedGroup.id,
                quantity,
                fromDate,
                toDate: endDate.toISOString(),
                customerDetails: {
                    firstName: personalDetails.firstName,
                    lastName: personalDetails.lastName,
                    phone: personalDetails.phone,
                    email: personalDetails.email,
                },
                comment: comment,
                ...attributionPayload(attribution),
                emailNote: emailNote,
                discount: discount,
                ...priceAdjustmentAudit,
                saveMode: mode,
            }).unwrap();
            dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails']));
            dispatch(clearBookingForm());
            setPriceAdjustmentReason('');
            setAttribution(EMPTY_BOOKING_ATTRIBUTION);

            // Saved — but a refused mail is said, never shown as a plain success.
            const mailBlocked = mailBlockedOf(created);
            setSnackbarData({
                open: true,
                ...savedOutcomeToast(t, mailBlocked, t('create_booking.response.success')),
            });

            if (mode === 'save_and_send_email') {
                setOpen(false);
            }

            setTimeout(() => {
                navigate(paths.rentalBooking);
            }, 1500);
        } catch (error: any) {
            Logger('CreateNonRoomBookingPage').error(`Error creating ${useCase} booking:`, error);
            setSnackbarData({
                open: true,
                message: errorMessageFrom(error, t('create_booking.response.failure'), t),
                severity: 'error',
            });
            if (mode === 'save_and_send_email') {
                setOpen(false);
            }
        } finally {
            if (mode === 'save_only') {
                setLoadingCreateBookingOnlySave(false);
            } else {
                setLoadingCreateBookingSaveAndSend(false);
            }
        }
    };

    const isFormValid = () => {
        return (
            personalDataValidation.firstName &&
            personalDataValidation.lastName &&
            personalDataValidation.email &&
            startDate &&
            startTime &&
            duration &&
            quantity >= 1 &&
            endDate &&
            selectedLocation &&
            selectedGroup
        );
    };

    // #2036: the admin UI must not offer what cannot work. A settled preview
    // error means the backend could not determine a price for this slot — it
    // refuses the write anyway, so letting the operator fill in the whole form
    // first only wastes their time. Same gate the edit dialog carries; create
    // was the half that got missed.
    //
    // Blocking on any settled error rather than only a 422 is deliberate: a
    // booking whose price could not be computed must not be created, whatever
    // the reason. The price box above already shows why.
    const saveBlockedByPrice = previewStatus === 'error';
    const saveButtonEnabled = isFormValid() && !saveBlockedByPrice;

    return (
        <Container maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} spacing={2} sx={{ mb: 3 }}>
                    <Typography variant={'h5'}>{t('create_booking.title')}</Typography>
                </Stack>
                <Paper sx={styles.paper}>
                    <Typography variant={'h3'} className={'mb-5'}>
                        {t('create_booking.reservation_details.title')}
                    </Typography>
                    <Grid
                        container
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Grid container spacing={2} className={'w-[100%] xl:w-[30%] mt-1 xl:mt-0 mb-8 xl:mb-0 flex justify-end items-end order-1 xl:order-2'}>
                            <NonRoomRentalReservationSummary
                                breakdown={breakdown}
                                previewStatus={previewStatus}
                                startDate={startDateTimeForSummary}
                                timeZone={timeZone}
                                endDate={endDate?.toISOString() ?? null}
                                locationName={selectedLocation?.name}
                                groupDescription={selectedGroup?.description}
                                groupLangNames={selectedGroup?.langNames}
                                discount={discount}
                                onDiscountChange={(val) => dispatch(setDiscount({ discount: val }))}
                            />
                            <PriceAdjustmentReasonField
                                discount={discount}
                                value={priceAdjustmentReason}
                                onChange={setPriceAdjustmentReason}
                            />
                        </Grid>
                        <Grid container spacing={2} className={'w-[100%] xl:w-[70%] flex justify-start items-start order-2 xl:order-1'}>
                            {loadingGroups ? (
                                <Grid item xs={12} className={'flex justify-center items-center'} sx={{ minHeight: '400px' }}>
                                    <CircularProgress />
                                </Grid>
                            ) : (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <SelectField
                                            options={locations?.map((loc: Location) => loc.name) ?? []}
                                            value={selectedLocation?.name ?? ''}
                                            onChange={onLocationChange}
                                            disabled={!locations || locations.length === 0}
                                            loading={loadingLocations}
                                            label={t('create_booking.reservation_details.location') + ' *'}
                                            noOptions={t(ui.noLocationsKey)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <SelectField
                                            options={groupOptions}
                                            value={selectedGroup?.name ?? ''}
                                            onChange={onGroupChange}
                                            disabled={loadingGroups || !assetGroups || assetGroups.length === 0}
                                            loading={loadingGroups}
                                            label={t(ui.groupLabelKey) + ' *'}
                                            noOptions={t(ui.noGroupsKey)}
                                        />
                                    </Grid>
                                    {fetchingAvailabilities ? (
                                        <Grid item xs={12} className={'flex justify-center items-center py-30'}>
                                            <CircularProgress />
                                        </Grid>
                                    ) : (
                                        <>
                                            <Grid item xs={12} sm={6}>
                                                <BookingDatePicker
                                                    value={startDate ? dayjs(startDate) : null}
                                                    onChange={(date) => handleStartDateChange(date)}
                                                    shouldDisableDate={() => false}
                                                    availableDates={availableStartDates}
                                                    label={t('create_booking.reservation_details.start_date') + ' *'}
                                                    disabled={!selectedGroup || fetchingAvailabilities}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <SelectField
                                                    options={availableTimeSlots}
                                                    value={startTime ?? ''}
                                                    onChange={handleStartTimeChange}
                                                    disabled={!startDate || availableTimeSlots.length === 0 || fetchingAvailabilities}
                                                    loading={fetchingAvailabilities}
                                                    label={t('create_booking.reservation_details.start_time') + ' *'}
                                                    noOptions={t('create_booking.reservation_details.no_times_available')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <SelectField
                                                    options={availableDurationOptions.map((opt) => opt.label)}
                                                    value={availableDurationOptions.find((opt) => opt.value === duration)?.label ?? ''}
                                                    onChange={(label) => {
                                                        const option = availableDurationOptions.find((opt) => opt.label === label);
                                                        if (option) handleDurationChange(option.value.toString());
                                                    }}
                                                    disabled={!startTime || availableDurationOptions.length === 0 || fetchingAvailabilities}
                                                    loading={fetchingAvailabilities}
                                                    label={t('create_booking.reservation_details.duration') + ' *'}
                                                    noOptions={t('create_booking.reservation_details.no_durations_available')}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <BookingDatePicker
                                                    value={endDate ? dayjs(endDate) : null}
                                                    onChange={() => {}}
                                                    shouldDisableDate={() => true}
                                                    availableDates={[]}
                                                    label={t('create_booking.reservation_details.end_date')}
                                                    disabled={true}
                                                />
                                            </Grid>
                                            {ui.showsOneUnitHint && (
                                                <Grid item xs={12}>
                                                    <p className={'text-xs text-foreground/60'}>
                                                        {t('create_booking.reservation_details.one_unit_per_booking')}
                                                    </p>
                                                </Grid>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-3'}>
                        {t('create_booking.customer_details.title')}
                    </Typography>
                    <Grid container={true} spacing={2} className={'w-[100%] xl:w-[70%]'} sx={{ justifyContent: 'flex-start' }}>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onFirstNameChange}
                                value={personalDetails.firstName}
                                label={t('create_booking.customer_details.first_name') + ' *'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onLastNameChange}
                                value={personalDetails.lastName}
                                label={t('create_booking.customer_details.last_name') + ' *'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                onChange={onEmailChange}
                                value={personalDetails.email}
                                label={t('create_booking.customer_details.email') + ' *'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <PhoneNumberComponent
                                initialValue={personalDetails.phone}
                                onChange={onPhoneChange}
                                valid={personalDataValidation.phone}
                                required={false}
                                label={t('create_booking.customer_details.phone')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <BookingAttributionFields
                                value={attribution}
                                onChange={setAttribution}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onCommentChange}
                                value={comment}
                                label={t('create_booking.customer_details.comment')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <InputField
                                onChange={onEmailNoteChange}
                                value={emailNote}
                                maxLength={BOOKING_EMAIL_NOTE_MAX_LENGTH}
                                label={t('create_booking.customer_details.email_note')}
                            />
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />

                    <div className={'flex space-x-2 mt-6'}>
                        <CustomButton title={t('create_booking.button.cancel')} onClick={() => navigate(paths.rentalBooking)} isFilled={false} />
                        <CustomButton
                            title={t('create_booking.button.confirm_only')}
                            onClick={() => {
                                handleCreateBooking('save_only');
                            }}
                            loading={loadingCreateBookingOnlySave}
                            disabled={!saveButtonEnabled || loadingCreateBookingOnlySave || loadingCreateBookingSaveAndSend}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t('create_booking.button.confirm_and_send')}
                            onClick={() => setOpen(true)}
                            isFilled={true}
                            disabled={
                                !saveButtonEnabled || !personalDataValidation.email
                                || loadingCreateBookingOnlySave || loadingCreateBookingSaveAndSend || !!mailRefusal
                            }
                        />
                    </div>
                    <CustomerMailBlockedNote refusal={mailRefusal} />
                    <SnackbarComponent open={snackbarData.open} message={snackbarData.message} severity={snackbarData.severity} onClose={handleCloseSnackbar} />
                    <CustomModal
                        open={open}
                        onClose={() => setOpen(false)}
                        title={t('create_booking.modal.title')}
                        saveButton={
                            <CustomButton
                                title={t('create_booking.modal.confirm_button')}
                                onClick={() => handleCreateBooking('save_and_send_email')}
                                disabled={!personalDataValidation.email || loadingCreateBookingSaveAndSend || saveBlockedByPrice || !!mailRefusal}
                                loading={loadingCreateBookingSaveAndSend}
                                isFilled={true}
                                fullWidth={false}
                            />
                        }
                        closeButton={<CustomButton title={t('create_booking.modal.cancel_button')} onClick={() => setOpen(false)} isFilled={false} />}
                        description={t('create_booking.modal.description')}
                    >
                        <></>
                    </CustomModal>
                </Paper>
            </Box>
        </Container>
    );
};

export default CreateNonRoomBookingPage;
